<template>
  <IbhFastbookingWrapper
    :initially-sticky-hidden="initiallyStickyHidden"
    :initially-hidden="initiallyHidden"
    :has-hide-sticky-button="hasHideStickyButton"
    :i18n="i18n"
    :always-sticky="alwaysSticky"
  >
    <div class="ibh-fastbooking__wrapper wrapper js-ibh-fastbooking-wrapper">
      <FastbookingTabs
        v-if="withTabs && isFastbookingReady"
        :i18n="i18n"
        :active-tab="activeTab"
        :flight-plus-hotel-data="flight_hotel_data"
        @update:tabs="handleUpdateTabs"
      />
    </div>
    <slot
      name="fastbookingform"
      :destination-id="destinationId"
      :check-in="checkIn"
      :check-out="checkOut"
      :promo-code="promoCode"
      :occupancy="occupancy"
      :render-room-input-name="renderRoomInputName"
      :render-age-input-name="renderAgeInputName"
      :destinia="destinia"
      :provider="provider"
      :departure-airport-iata="departureAirportIata"
      :departure-airport-name="departureAirportName"
    ></slot>
    <div v-if="activeTab == 'hotel'" class="wrapper">
      <ibh-fastbooking
        v-if="fbData"
        class="ibh-fastbooking-container__fastbooking"
        :data="fbData"
        :last-searches="LastSearches"
        :first-day-week="firstDayOfWeek"
        :locale="props.locale"
        :max-rooms="props.maxRooms"
        :date-format="currentDateFormat"
        :selected-destination="props.selectedDestination"
        :search="props.search"
        :show-more-rooms-link="props.showMoreRoomsLink"
        :show-promocode-input="props.showPromocodeInput"
        :show-destination-experiences="props.showDestinationExperiences"
        :promocode-url="props.promocodeUrl"
        :hide-continents="props.hideContinents"
        :show-back-arrow-in-panel="props.showBackArrowInPanel"
        @on-ready="handleFastbookingReady"
        @on-destination-change="handleDestinationChange"
        @on-dates-change="handleDatesChange"
        @on-occupancy-change="handleOccupancyChange"
        @on-promocode-change="handlePromocodeChange"
        @on-submit="handleSubmit"
        @on-more-rooms="handleMoreRooms"
      />
    </div>
    <div v-if="activeTab !== 'hotel'" class="wrapper">
      <fh-fastbooking
        v-if="fbData"
        class="ibh-fastbooking-container__fastbooking"
        :data="fbData"
        :locale="props.locale"
        :max-rooms="props.maxRooms"
        :date-format="currentDateFormat"
        :selected-destination="props.selectedDestination"
        :search="props.search"
        :flight-plus-hotel-data="flight_hotel_data"
        :airport-searcher="airportSearch"
        :promocode-url="props.promocodeUrl"
        :show-back-arrow-in-panel="props.showBackArrowInPanel"
        @on-more-rooms="handleMoreRooms"
        @on-ready="handleFastbookingReady"
        @on-destination-change="handleDestinationChange"
        @on-dates-change="handleDatesChange"
        @on-occupancy-change="handleOccupancyChange"
        @on-promocode-change="handlePromocodeChange"
        @on-submit="handleFHSubmit"
      />
      <FlightPlusHotelModal
        :i18n="i18n"
        :show-modal="showModal"
        @close="closeFlightAndHotelModal"
        @accept="handleAccept"
        @cancel="handleDisagreement"
      />
    </div>
    <PromocodeBanner v-if="!currentDevice.isMobile" :i18n />
  </IbhFastbookingWrapper>
  <div v-if="showOverlay" class="ibh-fastbooking__overlay" @click="handleCloseOverlay"></div>
</template>

<script setup>
import dayjs from 'dayjs'
import { computed, nextTick, onMounted, ref } from 'vue'

import { asyncGetData } from '../../../../core/iberapi/hotels_data/hotels_data'
import { currentDevice } from '../../../../core/utils/currentDevice'
import { getCurrentLocale, getPrefix } from '../../../../core/utils/locale'
import { openLoader } from '../../../../elements/wait_loading/wait_loading'
import {
  FASTBOOKING_EVENTS,
  IBH_FASTBOOKING_EVENTS,
  STANDARD_DATE_FORMAT,
  UtagEvents,
} from '../../../../shared/fastbooking/constants'
import { flightAndHotelModalEvents, initializeUtagEvents } from '../../../../shared/fastbooking/fastbooking.analytics'
import fastbooking from '../../../../shared/fastbooking/ibh_fastbooking'
import { IbeEndpoints, ibeRequest } from '../../../../utils/ibe_request'
import { currentPageType } from '../../../../utils/page_type_detector'
import { getDestinationIdFromSharedSearchUrl } from '../../../../utils/shared_search'
import { hideSkeleton } from '../../../../utils/skeleton'
import { getCookie } from '../../../../utils/utilities'

import FastbookingTabs from './FastbookingTabs.vue'
import FlightPlusHotelModal from './FlightPlusHotelModal.vue'
import IbhFastbookingWrapper from './IbhFastbookingWrapper.vue'
import PromocodeBanner from './PromocodeBanner.vue'

// --- PROPS & EMITS ---
const props = defineProps({
  locale: {
    type: String,
    required: true,
  },
  maxRooms: {
    type: Number,
    required: true,
  },
  dateFormat: {
    type: String,
    default: 'DD/MM/YYYY',
  },
  lastSearches: {
    type: Array,
    default: () => [],
  },
  firstDayWeek: {
    type: String,
    default: '',
  },
  selectedDestination: {
    type: String,
    default: '',
  },
  search: {
    type: String,
    default: '',
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  initiallyHidden: {
    type: Boolean,
    default: false,
  },
  initiallyStickyHidden: {
    type: Boolean,
    default: false,
  },
  hasHideStickyButton: {
    type: Boolean,
    default: false,
  },
  i18n: {
    type: Object,
    default: () => {},
  },
  withTabs: {
    type: Boolean,
    default: true,
  },
  showMoreRoomsLink: {
    type: Boolean,
    default: true,
  },
  showBackArrowInPanel: {
    type: Boolean,
    default: true,
  },
  showDestinationExperiences: {
    type: Boolean,
    default: true,
  },
  showPromocodeInput: {
    type: Boolean,
    default: true,
  },
  promocodeUrl: {
    type: String,
    default: '',
  },
  alwaysSticky: {
    type: Boolean,
    default: false,
  },
  hideContinents: {
    type: Boolean,
    default: false,
  },
})

const firstDayOfWeek = computed(() => {
  return props.firstDayWeek === 'monday' ? 2 : 1
})

// --- SET DATE FORMAT ---
const currentDateFormat = ref(props.dateFormat)

onMounted(() => {
  const dateFormat = document.documentElement.dataset.dateFormat

  if (dateFormat) {
    currentDateFormat.value = dateFormat
  }
})

// --- FASTBOOKING OVERLAY ---
const showOverlay = ref(false)

// --- FASTBOOKING TABS ---
const activeTab = ref('hotel')
function handleUpdateTabs(tab) {
  activeTab.value = tab
}

// --- FASTBOOKING FLIGHT PLUS HOTEL ---
const flight_hotel_data = ref(null)
const showModal = ref(false)

function closeFlightAndHotelModal() {
  showModal.value = false
}

function showFlightAndHotelModal() {
  showModal.value = true
}

function initFlightPlusHotelData() {
  const logged_messages = getCookie('logged_messages')

  const level_cookie = logged_messages ? logged_messages.split('true:')[1] : '1'

  const partnerQuery = new URLSearchParams(window.location.search)
  const partner = partnerQuery.get('cp') ?? IB.partner.getPartnerInfo()

  const params = {
    country: getCookie('x-country'),
    level: level_cookie === undefined || level_cookie === null || level_cookie === '' ? '1' : level_cookie,
    page_type: currentPageType.get(),
    partner: partner ?? null,
  }

  const cached_flight_hotel_data = sessionStorage.getItem('flight_hotel_data')
  const cached_flight_hotel_data_lang = sessionStorage.getItem('flight_hotel_data_lang')
  const cached_flight_hotel_data_country = sessionStorage.getItem('flight_hotel_data_country')
  const cached_flight_hotel_data_partner = sessionStorage.getItem('flight_hotel_data_partner')
  const lang = getCurrentLocale()

  if (
    cached_flight_hotel_data &&
    cached_flight_hotel_data_country &&
    cached_flight_hotel_data_country === params.country &&
    cached_flight_hotel_data_lang &&
    cached_flight_hotel_data_lang === lang &&
    cached_flight_hotel_data_partner &&
    cached_flight_hotel_data_partner === partner
  ) {
    flight_hotel_data.value = JSON.parse(cached_flight_hotel_data)
  } else {
    const url = ibeRequest.buildURL(IbeEndpoints.FLIGHTANDHOTELS, lang, params)
    ibeRequest
      .get(url)
      .then(data => {
        const saveData = {
          ...data,
          utag_flight_and_hotel_link: `${data.flight_and_hotel.url}${document.body.dataset.utagFlightAndHotelLink}`,
        }
        flight_hotel_data.value = saveData

        sessionStorage.setItem('flight_hotel_data', JSON.stringify(saveData))
        sessionStorage.setItem('flight_hotel_data_country', params.country)
        sessionStorage.setItem('flight_hotel_data_lang', lang)
        sessionStorage.setItem('flight_hotel_data_partner', partner)
      })
      .catch(error => console.error('GET Error:', error.message))
  }
}

// --- FASTBOOKING SELECTION ---
const destinationId = ref('')
const checkIn = ref('')
const checkOut = ref('')
const promoCode = ref('')
const occupancy = ref([])
const destinia = ref(null)
const provider = ref(null)
const departureAirportIata = ref('')
const departureAirportName = ref('')
const isFastbookingReady = ref(false)

// --- GET FASTBOOKING DATA ---
const LastSearches = ref([])
const fbData = ref(null)
asyncGetData().then(data => {
  fbData.value = data
  // --- LAST SEARCHES ---
  LastSearches.value = fastbooking.getLastSearches(fbData.value)
})

// --- PRELOAD FASTBOOKING SELECTION ---
function handleFastbookingReady() {
  if (currentPageType.isHome() || (currentPageType.isBookings() && !currentPageType.isSharedSearch())) {
    // load fastbooking data from storage
    fastbooking.applyFastbookingState('storage')
  } else if (currentPageType.isSharedSearch()) {
    const destinationId = getDestinationIdFromSharedSearchUrl(document.referrer)

    if (!destinationId) return

    fastbooking.applyFastbookingStateFromLastSearch(destinationId)
  } else if (currentPageType.isHotel() && currentPageType.fromBookings()) {
    fastbooking.applyFastbookingStateDates('storage')
  }

  fastbooking.setAsInitialized()
  isFastbookingReady.value = true

  // emit event
  const ibhFbReady = new CustomEvent(IBH_FASTBOOKING_EVENTS.FASTBOOKING_READY, {
    detail: activeTab.value,
  })

  document.dispatchEvent(ibhFbReady)

  hideSkeleton(fastbooking.mainWrapper)
}

// --- INPUT NAMES ---
const renderRoomInputName = (counter, type) => `vo_booking[rooms][${counter}][${type}_count]`
const renderAgeInputName = counter => `vo_booking[rooms][${counter}][ages][]`

// --- INPUT HANDLERS ---
function handleDestinationChange(destination) {
  if (!destination) {
    fastbooking.setFastbookingState({ place_to_go: null })
    return
  }

  fastbooking.setFastbookingState({
    place_to_go: {
      id: destination.id.slice(1), // without initial letter
      name: destination.title,
      type: destination.type,
      id_and_type: destination.id,
    },
  })
}

function handleDatesChange(dates) {
  if (!dates.checkIn || !dates.checkOut) {
    fastbooking.setFastbookingState({ dates: null })
    return
  }

  fastbooking.trigger(FASTBOOKING_EVENTS.DATES_CHANGED)

  fastbooking.setFastbookingState({
    dates: {
      check_in: dayjs(dates.checkIn).format(STANDARD_DATE_FORMAT),
      check_out: dayjs(dates.checkOut).format(STANDARD_DATE_FORMAT),
    },
  })
}

function handleOccupancyChange(rooms) {
  let totalAdults = 0
  let totalChildren = 0
  const roomsOccupancy = {}

  for (let index = 0; index < rooms.length; index++) {
    totalAdults += rooms[index].number_adults
    totalChildren += rooms[index].number_kids
    roomsOccupancy[index] = {
      adults_count: rooms[index].number_adults,
      children_count: rooms[index].number_kids,
      ages: rooms[index].kidsAges,
    }
  }

  fastbooking.setFastbookingState({
    people: {
      adults: totalAdults,
      children: totalChildren,
      total: totalAdults + totalChildren,
    },
    rooms_info: roomsOccupancy,
  })
}

function handlePromocodeChange(promocode) {
  fastbooking.setFastbookingState({
    promocode: {
      code: promocode ?? '',
    },
  })
}

function submitForm() {
  const formElement = document.getElementById('new_vo_booking')
  if (formElement) {
    formElement.submit()
    openLoader()
  }
}

function handleAccept() {
  flightAndHotelModalEvents('confirmation')
  const submitEvent = new CustomEvent(UtagEvents.Submit)
  document.dispatchEvent(submitEvent)
  submitForm()
}

function handleDisagreement() {
  flightAndHotelModalEvents('disagreement')
}

// --- SUBMIT ---
function handleSubmit(fastbookingSelection) {
  const {
    destination: fbDestination,
    checkIn: fbCheckIn,
    checkOut: fbCheckOut,
    promocode: fbPromoCode,
    occupancy: fbOccupancy,
  } = fastbookingSelection

  provider.value = null
  destinia.value = null
  departureAirportIata.value = null
  departureAirportName.value = null

  if (fbDestination) destinationId.value = fbDestination
  if (fbCheckIn) checkIn.value = fbCheckIn
  if (fbCheckOut) checkOut.value = fbCheckOut
  if (fbCheckIn && fbCheckOut && fbPromoCode) promoCode.value = fbPromoCode
  if (fbOccupancy.length) {
    occupancy.value = []
    fbOccupancy.forEach(room => {
      occupancy.value.push({
        adults: room.number_adults,
        children: room.number_kids,
        ages: room.kidsAges,
      })
    })
  }
  // If fastbooking is submited without dates, clear promo-code input
  if (!fbCheckIn && !fbCheckOut && fbPromoCode) {
    fastbooking.setFastbookingState({
      promocode: {
        code: '',
      },
    })
  }

  fastbooking.storeLastSearchInLocalStorage(fastbookingSelection, fbData.value)

  nextTick(() => {
    const formElement = document.getElementById('new_vo_booking')
    if (formElement) {
      const submitEvent = new CustomEvent(UtagEvents.Submit)
      document.dispatchEvent(submitEvent)

      fastbooking.setFastbookingState({
        origin: null,
      })
      fastbooking.storeFastbookingStateInLocalStorage()
      submitForm()
    }
  })
}

function handleFHSubmit(fastbookingSelection) {
  const {
    destination: fbDestination,
    checkIn: fbCheckIn,
    checkOut: fbCheckOut,
    promocode: fbPromoCode,
    occupancy: fbOccupancy,
    origin: fbOrigin,
  } = fastbookingSelection

  if (fbDestination) destinationId.value = fbDestination
  if (fbCheckIn) checkIn.value = fbCheckIn
  if (fbCheckOut) checkOut.value = fbCheckOut
  if (fbCheckIn && fbCheckOut && fbPromoCode) promoCode.value = fbPromoCode
  if (fbOccupancy.length) {
    occupancy.value = []
    fbOccupancy.forEach(room => {
      occupancy.value.push({
        adults: room.number_adults,
        children: room.number_kids,
        ages: room.kidsAges,
      })
    })
  }
  // If fastbooking is submited without dates, clear promo-code input
  if (!fbCheckIn && !fbCheckOut && fbPromoCode) {
    fastbooking.setFastbookingState({
      promocode: {
        code: '',
      },
    })
  }

  if (fbOrigin) {
    provider.value = flight_hotel_data.value.flight_and_hotel.provider
    destinia.value = fbOrigin.title
    departureAirportIata.value = fbOrigin.iata
    departureAirportName.value = fbOrigin.title
  }

  nextTick(() => {
    const formElement = document.getElementById('new_vo_booking')
    if (formElement) {
      fastbooking.storeFastbookingStateInLocalStorage()
      showFlightAndHotelModal()
    }
  })
}

function handleMoreRooms() {
  IB.phone_list.openCallCenterPopup()
}

function handleOverlay() {
  document.addEventListener(FASTBOOKING_EVENTS.FASTBOOKING_ACTIVE, e => {
    const data = e.detail

    if (currentDevice.isMobile) {
      if (currentPageType.isHome() || (!currentPageType.isHome() && data)) {
        showOverlay.value = true
        document.querySelector('.headband')?.classList.add('hidden')
        document.querySelector('.mobile-header')?.classList.add('hidden')
      }
      if (currentPageType.isHome()) document.querySelector('body').classList.add('is-fullscreen')
    } else {
      showOverlay.value = true
    }
  })
  document.addEventListener(FASTBOOKING_EVENTS.FASTBOOKING_INACTIVE, e => {
    const data = e.detail

    if (currentDevice.isMobile) {
      if (currentPageType.isHome() || (!currentPageType.isHome() && data)) {
        showOverlay.value = false
        document.querySelector('.headband')?.classList.remove('hidden')
        document.querySelector('.mobile-header')?.classList.remove('hidden')
      }
      if (currentPageType.isHome()) document.querySelector('body').classList.remove('is-fullscreen')
    } else {
      showOverlay.value = false
    }
  })
}

function handleCloseOverlay() {
  showOverlay.value = false
  fastbooking.closePanels()
}

async function airportSearch(text) {
  const search = text.trim().toLowerCase()
  const response = await fetch(`${getPrefix()}/ajax_contents/search_airport/?q=${search}&provider=Destinia`)
  const data = await response.json()
  return data
}

onMounted(() => {
  initializeUtagEvents()
  handleOverlay()
  if (props.withTabs) {
    initFlightPlusHotelData()
  }

  document.addEventListener(IBH_FASTBOOKING_EVENTS.CHANGE_TAB, event => {
    handleUpdateTabs(event.detail.tab)
  })

  fastbooking.setFastbookingMainContainer('#ibh-fastbooking-container')
})
</script>

<style lang="scss">
@use '../../../../base_css/abstracts/abstracts.scss' as *;

.ibh-fastbooking-container {
  width: 100%;

  &__fastbooking {
    box-shadow: 0 12px 17px 3px rgba(0, 0, 0, 4%);
  }
}

.ibh-fastbooking {
  position: relative;
  z-index: 9000;

  &__wrapper {
    position: relative;
  }

  &__close {
    position: absolute;
    top: 10px;
    right: 20px;
    z-index: 9;
  }

  &__mobile-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 10px 20px;
    margin-bottom: 10px;
  }

  &__mobile-title {
    color: var(--c_blue_700);
    font-weight: bold;
    font-size: 24px;
    /* stylelint-disable-next-line
    font-family-no-missing-generic-family-keyword */
    font-family: fastbooking;
  }
}
</style>
